import { usePolledCounts } from '@vello/web-theme';
import { screenAllowed, type NavCount } from '../../src/index';
import { useAdmin } from '../session';

/**
 * The shell's readiness signal (D615 SETUP-READINESS): how many REQUIRED items of the platform setup still ask something of the
 * operator, for the menu badge and the bell. One `GET /v1/admin/setup-readiness` for the scope, every 5 minutes while the tab is
 * visible (a hidden tab asks nothing; the setup changes rarely), and again when the scope or the grants change.
 *
 * The count is the server's `summary.pending`. A failed or refused read — or an operator whose grants never open the screen —
 * has no number at all: no badge, never a 0.
 */
export const SETUP_POLL_MS = 5 * 60_000;

export interface SetupPendingState {
  readonly status: 'loading' | 'ready' | 'failed';
  /** The pending required items; `undefined` unless `status` is `ready`. */
  readonly pending: number | undefined;
}

export function useSetupPending(): SetupPendingState {
  const { runtime, scope, grants } = useAdmin();
  const market = scope.current.city;
  const country = scope.current.country;
  const enabled = screenAllowed(grants, 'platform_setup');
  const state = usePolledCounts<'pending', NavCount>(
    async () => {
      const res = await runtime.setupReadiness.get(market, country);
      return res.ok ? { pending: { count: res.value.summary.pending, atLeast: false } } : undefined;
    },
    { intervalMs: SETUP_POLL_MS, enabled, resetKey: `${country ?? ''}|${market ?? ''}|${grants === undefined ? 'unknown' : JSON.stringify(grants)}` },
  );
  const count = state.values.pending?.count;
  return { status: state.status, pending: state.status === 'ready' ? count : undefined };
}
